import { Badge } from "./Badge";
import { googleChannelTypeLabel, googleStrengthMeta, yen } from "../lib/meta";
import type { Campaign, GoogleAccountCheck } from "../types/contract";

/**
 * Google 広告 運用チェック（塩田さん推奨設定 2026-10-09）。
 * 予算配分（日予算合計→月換算）・主コンバージョン・キャンペーンごとの入稿/学習チェックを1枚で見る。
 * データは GET /google/campaigns の google.ops / account（worker が GAQL で算出）。ここでは判定を再計算しない。
 */
interface GoogleOpsCardProps {
  campaigns: Campaign[];
  account: GoogleAccountCheck | undefined;
  /** 月換算の係数（日予算×30.4）。Google の月間上限の考え方に合わせる。 */
  monthlyFactor?: number;
}

const MONTHLY_GUIDE_JPY = 2_000_000; // 塩田さん推奨: 検索3万＋P-MAX3.7万＝月200万円に収まる配分

export function GoogleOpsCard({ campaigns, account, monthlyFactor = 30.4 }: GoogleOpsCardProps) {
  const google = campaigns.filter((c) => c.platform === "google");
  if (google.length === 0) return null;
  const activeDaily = google.filter((c) => c.status === "ACTIVE").reduce((a, c) => a + (c.daily_budget ?? 0), 0);
  const allDaily = google.reduce((a, c) => a + (c.daily_budget ?? 0), 0);
  const monthlyAll = Math.round(allDaily * monthlyFactor);
  const monthlyActive = Math.round(activeDaily * monthlyFactor);

  return (
    <section className="gops-card" aria-label="Google広告 運用チェック">
      <div className="gops-head">
        <span className="gops-title">Google広告 運用チェック</span>
        <span className="gops-kv">
          日予算合計 <b>{yen(allDaily)}</b>（配信中のみ {yen(activeDaily)}）
        </span>
        <span className="gops-kv">
          月換算 <b>{yen(monthlyAll)}</b>（配信中のみ {yen(monthlyActive)}）／目安 {yen(MONTHLY_GUIDE_JPY)}
          {monthlyAll > MONTHLY_GUIDE_JPY && <Badge label="目安超過" color="var(--warn)" bg="var(--warn-bg)" />}
        </span>
        <span className="gops-kv">
          主コンバージョン{" "}
          {account ? (
            account.purchase_only ? (
              <Badge label="購入のみ" color="var(--st-active-c)" bg="var(--st-active-bg)" />
            ) : (
              <>
                <Badge label="購入以外が混在" color="var(--warn)" bg="var(--warn-bg)" />{" "}
                <span title={account.primary_conversions.join(" / ")}>{account.primary_conversions.join(" / ")}</span>
              </>
            )
          ) : (
            "—"
          )}
        </span>
      </div>

      <div className="gops-grid">
        {google.map((c) => {
          const ops = c.google?.ops;
          const strength = googleStrengthMeta(ops?.ad_strength.min);
          const isPmax = c.google?.channel_type === "PERFORMANCE_MAX";
          const learning = ops?.learning;
          const videosOk = !isPmax || (ops?.videos.count ?? 0) >= 2;
          return (
            <div className="gops-item" key={c.id}>
              <div className="gops-item-name">
                {c.name}{" "}
                <span className="cell-sub" style={{ display: "inline" }}>
                  {googleChannelTypeLabel(c.google?.channel_type)} · 日予算 {c.daily_budget != null ? yen(c.daily_budget) : "—"}
                </span>
              </div>
              {ops ? (
                <ul className="gops-checks">
                  <li className={learning?.active ? "ng" : "ok"}>
                    <span className="mark">{learning?.active ? "⏳" : "✓"}</span>
                    <span>
                      学習期間（開始から14日）:{" "}
                      {learning?.active
                        ? `静観中・残り${learning.days_left ?? "?"}日（予算・入札・アセットを大きく変えない）`
                        : learning?.start && learning.until
                          ? c.status === "ACTIVE" || (learning.days_left ?? 0) <= 0
                            ? `終了（${learning.until}）`
                            : `${learning.start} 開始予定 → ${learning.until} まで静観`
                          : "開始日未設定"}
                    </span>
                  </li>
                  <li className={strength.ok ? "ok" : "ng"}>
                    <span className="mark">{strength.ok ? "✓" : "!"}</span>
                    <span>
                      Ad Strength（広告の有効性）: {strength.label}
                      {ops.ad_strength.items.length > 1 && (
                        <>
                          {" "}
                          <span className="platform-note">
                            （{ops.ad_strength.items.map((it) => `${it.name}=${googleStrengthMeta(it.strength).label.replace("有効性 ", "")}`).join(" / ")}）
                          </span>
                        </>
                      )}
                      {" "}— 目標は「高い」以上
                    </span>
                  </li>
                  <li className={ops.text.headlines_max && ops.text.descriptions_max ? "ok" : "ng"}>
                    <span className="mark">{ops.text.headlines_max && ops.text.descriptions_max ? "✓" : "!"}</span>
                    <span>見出し・説明文を上限まで入力: {ops.text.detail}</span>
                  </li>
                  {isPmax && (
                    <li className={videosOk ? "ok" : "ng"}>
                      <span className="mark">{videosOk ? "✓" : "!"}</span>
                      <span>
                        動画アセット: {ops.videos.count ?? 0}本（縦型と横型の両方が必要。向きはGoogle広告の管理画面で確認）
                      </span>
                    </li>
                  )}
                </ul>
              ) : (
                <div className="platform-note">運用チェックのデータがありません。</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="gops-note">
        API で設定できない項目（Google広告の管理画面で確認）: P-MAX の「最終ページURLの拡張」と「テキストアセットの自動生成」は OFF
        ／ 自動適用のおすすめは OFF ／ 通話アセットなし ／ YouTube 系コンバージョン（follow-on views 等）は「補助」。
        予算・入札戦略（コンバージョン数の最大化）・キーワードの変更は学習期間後に。
      </div>
    </section>
  );
}
